import { Opportunity } from "@entities";
import { Message01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link } from "@mui/icons-material";
import BarChartIcon from "@mui/icons-material/BarChart";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import HailRoundedIcon from "@mui/icons-material/HailRounded";
import GroupsIcon from "@mui/icons-material/Groups";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import PeopleIcon from "@mui/icons-material/People";
import RuleIcon from "@mui/icons-material/Rule";
import CategoryIcon from "@mui/icons-material/Category";
import SettingsIcon from "@mui/icons-material/Settings";
import SmsIcon from "@mui/icons-material/Sms";
import ThumbsUpDownIcon from "@mui/icons-material/ThumbsUpDown";
import TodayIcon from "@mui/icons-material/Today";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HandshakeIcon from "@mui/icons-material/Handshake";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import { opportunityRepo } from "@repositories";
import { canManageBackgroundImages } from "@shared/config/background-image-config";
import { Roles } from "@shared/Roles";
import { linko } from "@shared/linko";
import { useQuery } from "@tanstack/react-query";
import { Box } from "@mui/material";
import { useEffect, useMemo, useState, type FC } from "react";
import { isMobile } from "react-device-detect";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { remult } from "remult";
import { CallCenterCoordinatorAlerts } from "../call-center/CallCenterCoordinatorAlerts";
import { CallCenterShiftProvider } from "../../contexts/CallCenterShiftProvider";
import { CallCenterRoot } from "../../pages/call-center/CallCenterRoot";
import { useNotifications } from "../../contexts/NotificationProvider";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useUser } from "../../contexts/UserProvider";
import { Offline } from "../../pages/offline/Offline";
import Footer from "../footer/Footer";
import { Sidebar, type SideBarButton } from "../sidebar/SideBar";

export const AdminLayout: FC = () => {
  const { pathname } = useLocation();
  const { notifications, unreadCount } = useNotifications();
  const [buttons, setButtons] = useState<(SideBarButton | undefined)[]>([]);
  const [isOffline, setIsOffline] = useState(() => !navigator.onLine);
  const { currOrganization } = useOrganization();
  const { currentUser, updatedCurrentUser } = useUser();
  const navigate = useNavigate();

  const { data: requestsCount = 0, isLoading: isRequestLoading } = useQuery({
    queryKey: ["requestsCount", currOrganization?.id, currentUser?.id],
    queryFn: () => {
      return opportunityRepo.count({
        ...Opportunity.hyperFilter({
          isDeleted: false,
          $or: [
            remult.isAllowed(Roles.Admins)
              ? {}
              : {
                form: {
                  $is: {
                    $or: [
                      {
                        team: {
                          $is: {
                            id: currentUser?.teams?.map(
                              (team: any) => team.teamId,
                            ),
                          },
                        },
                      },
                    ],
                  },
                },
              },
          ],
          status: "AID_REQUEST",
        }),
      });
    },
    enabled: !!currOrganization?.id,
    staleTime: 1000 * 60 * 30, // Cache results for 30 minutes
  });

  const { data: openingHubAccess } = useQuery({
    queryKey: ["opening-hub-config-access", currentUser?.id],
    queryFn: async () => {
      const { data } = await linko.openingHubConfig.queries.getAccess();
      return data;
    },
    enabled: !!currentUser?.id && remult.isAllowed(Roles.AdminsAndManager),
    staleTime: 1000 * 60 * 2,
  });

  const canEditOpeningHub =
    remult.isAllowed(Roles.SuperDuperAdmin) || !!openingHubAccess?.canEdit;
  const canOpenSettings = canManageBackgroundImages(
    currentUser?.phoneNumber || remult.user?.phoneNumber,
    remult.user?.roles,
  );
  const settingsButton = useMemo<SideBarButton | undefined>(
    () =>
      canOpenSettings
        ? {
          title: "הגדרות",
          icon: <SettingsIcon />,
          navigate: "/settings",
        }
        : undefined,
    [canOpenSettings],
  );

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  useEffect(() => {
    if (currentUser?.id && remult.isAllowed(Roles.AdminsAndManager))
      updatedCurrentUser("lastEntry", new Date());
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currOrganization && !remult.isAllowed(Roles.SuperDuperAdmin)) return;

    if (remult.isAllowed(Roles.SuperDuperAdmin)) {
      setButtons([
        {
          title: "ארגונים",
          icon: <VolunteerActivismIcon />,
          navigate: "/organizations",
        },
        {
          title: "ארגון",
          icon: <TodayIcon />,
          navigate: "/organization",
        },
        {
          title: "אקסלים",
          icon: <UploadFileIcon />,
          navigate: "/upload-excel",
        },
        {
          title: "יצירת לינקים",
          icon: <Link />,
          navigate: "/create-links",
        },
        {
          title: "מסך פתיחה / חירום",
          icon: <WarningAmberRoundedIcon />,
          navigate: "/opening-hub-settings",
        },
        {
          title: "נראות קטגוריות",
          icon: <CategoryIcon />,
          navigate: "/category-visibility",
        },
        {
          title: "ארגונים שותפים",
          icon: <HandshakeIcon />,
          navigate: "/partner-organizations",
        },
        {
          title: "ארגוני מתנדבים",
          icon: <ApartmentIcon />,
          navigate: "/volunteer-organizations",
        },
        settingsButton,
      ]);
      return;
    }

    setButtons([
      {
        title: "מתנדבים",
        icon: <PeopleIcon />,
        navigate: "/volunteers",
        moreRoutes: ["teams", "/volunteers/"],
      },
      {
        title: "קבוצות",
        icon: <GroupsIcon />,
        navigate: "/volunteers-groups",
        moreRoutes: ["/volunteers-groups"],
      },
      {
        title: "קהילות",
        icon: <Diversity3Icon />,
        navigate: "/communities",
        moreRoutes: ["/communities"],
      },
      {
        title: "משימות",
        icon: <EventNoteRoundedIcon />,
        navigate: "/events",
        moreRoutes: ["events/new", "/events/"],
      },
      currOrganization?.hasHoursReporting
        ? {
          navigate: "/events-reports",
          icon: <RuleIcon />,
          moreRoutes: ["/user-hours"],
          children: [
            {
              navigate: "/user-hours",
              icon: <WatchLaterIcon />,
              title: "דיווחי שעות",
            },
            {
              navigate: "/events-reports",
              icon: <ThumbsUpDownIcon />,
              title: "דיווחי ביצוע",
            },
          ],
          title: "דיווחים",
        }
        : {
          navigate: "/events-reports",
          icon: <ThumbsUpDownIcon />,
          title: "דיווחי ביצוע",
        },
      currOrganization?.isAcceptRequest
        ? {
          title: "בקשות ומבקשי סיוע",
          moreRoutes: ["forms", "aid-related", "user-requests"],
          navigate: "/user-requests",
          icon: (
            <div className="notification-button-container">
              <HailRoundedIcon sx={{ fontSize: "1.75rem" }} />
              {requestsCount > 0 && (
                <p className="new-notifications-amount">{requestsCount}</p>
              )}
            </div>
          ),
        }
        : undefined,
      {
        title: "התראות",
        icon: (
          <div className="notification-button-container">
            {isMobile ? (
              <HugeiconsIcon icon={Message01Icon} strokeWidth={2} />
            ) : (
              <SmsIcon />
            )}
            {unreadCount > 0 && (
              <p className="new-notifications-amount">{unreadCount}</p>
            )}
          </div>
        ),
        navigate: "/notifications",
      },
      remult.isAllowed(Roles.AdminsAndManager)
        ? {
          title: "מוקד",
          icon: <SupportAgentRoundedIcon />,
          navigate: "/call-center-desk",
        }
        : undefined,
      remult.isAllowed(Roles.AdminsAndManager)
        ? {
          title: "סטטיסטיקות",
          icon: <BarChartIcon />,
          navigate: "/statistics",
        }
        : undefined,
      canEditOpeningHub
        ? {
          title: "מסך פתיחה / חירום",
          icon: <WarningAmberRoundedIcon />,
          navigate: "/opening-hub-settings",
        }
        : undefined,
      remult.isAllowed(Roles.AdminsAndManager)
        ? {
          title: "ארגונים שותפים",
          icon: <HandshakeIcon />,
          navigate: "/partner-organizations",
        }
        : undefined,
      remult.isAllowed(Roles.AdminsAndManager)
        ? {
          title: "ארגוני מתנדבים",
          icon: <ApartmentIcon />,
          navigate: "/volunteer-organizations",
        }
        : undefined,
      remult.isAllowed(Roles.SuperAdmins)
        ? {
          title: "נראות קטגוריות",
          icon: <CategoryIcon />,
          navigate: "/category-visibility",
        }
        : undefined,
      settingsButton,
    ]);
  }, [
    currOrganization,
    notifications,
    currentUser,
    remult.user,
    isRequestLoading,
    canEditOpeningHub,
    settingsButton,
  ]);

  const stationOnTasks =
    pathname === "/events" || pathname.startsWith("/events/");

  useEffect(() => {
    if (!currentUser?.isCallCenterStation) return;
    const onStation = pathname === "/call-center";
    if (!onStation && !stationOnTasks) {
      navigate("/call-center", { replace: true });
    }
  }, [currentUser?.isCallCenterStation, pathname, stationOnTasks, navigate]);

  if (currentUser?.isCallCenterStation) {
    return (
      <CallCenterShiftProvider>
        {stationOnTasks ? (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2,
                py: 1,
                borderBottom: "1px solid rgba(5,32,76,0.12)",
                bgcolor: "#fff",
              }}
            >
              <Box sx={{ fontWeight: 700, color: "#05204C" }}>טבלת המשימות</Box>
              <Box
                component="button"
                type="button"
                onClick={() => navigate("/call-center")}
                sx={{
                  border: 0,
                  cursor: "pointer",
                  borderRadius: 99,
                  px: 2,
                  py: 1,
                  fontWeight: 700,
                  color: "#fff",
                  bgcolor: "#05204C",
                }}
              >
                חזרה לעמדה
              </Box>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <Outlet />
            </Box>
          </Box>
        ) : (
          <CallCenterRoot />
        )}
      </CallCenterShiftProvider>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minWidth: 0,
      }}
    >
      <CallCenterCoordinatorAlerts />
      <div className="big-container" style={{ flex: 1, minHeight: 0 }}>
      {isOffline && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            bgcolor: "background.paper",
          }}
        >
          <Offline
            onRetry={() => setIsOffline(!navigator.onLine)}
            onContinue={() => setIsOffline(false)}
          />
        </Box>
      )}
      {isMobile ? (
        <>
          <Outlet />
          <Footer buttons={buttons.filter(Boolean) as SideBarButton[]} />
        </>
      ) : (
        <>
          <Sidebar buttons={buttons.filter(Boolean) as SideBarButton[]} />
          <div className="admin-main-content">
            <Outlet />
          </div>
        </>
      )}
      </div>
    </div>
  );
};
